import React, { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useRouteError } from "react-router-dom";
import { Sentry } from "../sentry";

interface State {
  hasError: boolean;
}

function isDevErrorBoundaryPreview(): boolean {
  return (
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(window.location.search).has("previewErrorBoundary")
  );
}

export function ErrorFallbackView() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        px: 3,
        bgcolor: "#fff",
      }}
    >
      <Box
        component="img"
        src="/logo.png"
        alt="Corpore"
        sx={{
          width: 214,
          mb: 0.5,
          // logo.png è scuro: lo porto sul viola bottone (#6B35AD)
          filter:
            "brightness(0) saturate(100%) invert(27%) sepia(64%) saturate(1804%) hue-rotate(239deg) brightness(91%) contrast(93%)",
        }}
      />
      <Box
        component="img"
        src="/corpore-crash.png"
        alt=""
        sx={{
          width: "min(420px, 92vw)",
          height: "auto",
          display: "block",
        }}
      />
      <Typography
        sx={{
          fontFamily: "'Nunito', sans-serif",
          fontWeight: 800,
          fontSize: { xs: 32, sm: 40 },
          letterSpacing: "-0.03em",
          textAlign: "center",
          color: "#28083B",
        }}
      >
        Ops... giornata dura!
      </Typography>
      <Typography
        sx={{
          color: "#5b5163",
          textAlign: "center",
          maxWidth: 360,
          fontFamily: "'Nunito', sans-serif",
          fontSize: 16,
          lineHeight: 1.45,
        }}
      >
        Qualcosa è andato storto, ricarica per risolvere il problema.
      </Typography>
      <Button
        variant="contained"
        onClick={() => window.location.reload()}
        sx={{
          mt: 1.5,
          bgcolor: "#6B35AD",
          textTransform: "none",
          fontFamily: "'Nunito', sans-serif",
          fontWeight: 700,
          borderRadius: 2,
          px: 3,
          py: 1.1,
          "&:hover": { bgcolor: "#5a2c92" },
        }}
      >
        Ricarica la pagina
      </Button>
    </Box>
  );
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  render() {
    if (isDevErrorBoundaryPreview() || this.state.hasError) {
      return <ErrorFallbackView />;
    }

    return this.props.children;
  }
}

/** Sostituisce la pagina bianca di React Router (stack + "Errore imprevisto"). */
export function RouteErrorFallback() {
  const error = useRouteError();

  useEffect(() => {
    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }
    Sentry.captureException(new Error(String(error)));
  }, [error]);

  return <ErrorFallbackView />;
}
